// ==================== VENDORS SERVICE ====================
// Extracted from database.js - contains all vendors (accounts payable) functionality

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../utils/database/firebase-config';
// NOTE: Service dependencies commented out to avoid circular imports during refactoring

export const vendorsService = {
  async getVendors(companyId) {
    // Get all vendor data including payment summaries
    const vendorsRef = collection(db, 'vendors');
    const q = query(vendorsRef, where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const vendors = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Enrich with payment summaries
    const enrichedVendors = await Promise.all(
      vendors.map(async (vendor) => {
        try {
          const paymentHistory = await this.getVendorPaymentHistory(companyId, vendor.id);
          const totalPaid = paymentHistory.reduce((sum, payment) => sum + (payment.amount || 0), 0);
          
          // Get bills for this vendor
          const billsRef = collection(db, 'bills');
          const billsQuery = query(billsRef, 
            where('vendorId', '==', vendor.id),
            where('companyId', '==', companyId)
          );
          const billsSnapshot = await getDocs(billsQuery);
          const bills = billsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          // Calculate accurate totals using totalAmount instead of amount
          const totalBilled = bills.reduce((sum, bill) => sum + (bill.totalAmount || 0), 0);
          const totalOwed = bills.reduce((sum, bill) => {
            const paidAmount = bill.paidAmount || 0;
            const totalAmount = bill.totalAmount || 0;
            return sum + Math.max(0, totalAmount - paidAmount);
          }, 0);
          
          // Calculate remaining amount (outstanding balance)
          const remainingAmount = totalBilled - totalPaid;
          
          return {
            ...vendor,
            totalPaid,
            totalBilled,
            totalOwed,
            remainingAmount,
            outstanding: remainingAmount, // For consistency with existing code
            balance: remainingAmount, // For consistency with existing code
            billCount: bills.length,
            paymentCount: paymentHistory.length,
            totalBills: bills.length,
            totalPayments: paymentHistory.length,
            totalSpent: totalBilled
          };
        } catch (error) {
          console.error('Error enriching vendor data:', error);
          return {
            ...vendor,
            totalPaid: 0,
            totalBilled: 0,
            totalOwed: 0,
            remainingAmount: 0,
            outstanding: 0,
            balance: 0,
            billCount: 0,
            paymentCount: 0,
            totalBills: 0,
            totalPayments: 0,
            totalSpent: 0
          };
        }
      })
    );
    
    return enrichedVendors;
  },

  async getVendor(vendorId) {
    const vendorRef = doc(db, 'vendors', vendorId);
    const vendorSnap = await getDoc(vendorRef);
    if (vendorSnap.exists()) {
      return { id: vendorSnap.id, ...vendorSnap.data() };
    }
    throw new Error('Vendor not found');
  },

  async addVendor(vendorData) {
    const vendorsRef = collection(db, 'vendors');
    const docRef = await addDoc(vendorsRef, {
      ...vendorData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive: true
    });
    return docRef.id;
  },

  async updateVendor(vendorId, vendorData) {
    const vendorRef = doc(db, 'vendors', vendorId);
    await updateDoc(vendorRef, {
      ...vendorData,
      updatedAt: serverTimestamp()
    });
    return true;
  },

  async deleteVendor(vendorId) {
    const vendorRef = doc(db, 'vendors', vendorId);
    await deleteDoc(vendorRef);
    return true;
  },

  async getVendorPaymentHistory(companyId, vendorId) {
    const paymentsRef = collection(db, 'vendorPayments');
    const q = query(paymentsRef, 
      where('companyId', '==', companyId),
      where('vendorId', '==', vendorId),
      orderBy('paymentDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }
}; 